import * as IoC from 'typescript-ioc';
import { IBlueprintsDAO, IEventBlueprintDTO, IMeetingTemplateDTO } from '../blueprints/blueprints.dao';
import { IApiClient, RequestContentType } from '../../infrastructure/api-client';
import * as BLUEPRINT_API from '../../constants/api/blueprints.constants';
import { BlueprintType, IBlueprintDTO, IOrderBookingBlueprintDTO } from '../blueprints/blueprint.types';
import { IConnectionTemplateDTO } from '../blueprints/connection.template.types';
import { ICreateOrderTemplateDTO, IOrderTemplateDetailsDTO, IOrderTemplateDTO } from '../blueprints/order.template.types';
import { IAssetUrlDTO, S3ObjectMetadata } from '../shared.dto';


class BlueprintsDAO extends IBlueprintsDAO {
    constructor(@IoC.Inject private readonly apiClient: IApiClient) {
        super();
    }

    public async createBlueprint<T extends IBlueprintDTO, K extends IBlueprintDTO>(params: T): Promise<K> {
        const url = `account/${params.accountId}/blueprint`;
        const response = await this.apiClient.post<K>(url, params);

        return response.parsedResponse;
    }

    public async editBlueprint<T extends IBlueprintDTO, K extends IBlueprintDTO>(params: T): Promise<K> {
        const url = `account/${params.accountId}/blueprint/${params.blueprintId}`;
        const response = await this.apiClient.put<K>(url, params);

        return response.parsedResponse;
    }

    public async getBlueprint<T extends IBlueprintDTO>(params: { accountId: number; blueprintId: string }): Promise<T> {
        const response = await this.apiClient.get<T>(BLUEPRINT_API.GET_BLUEPRINT_URL, params);

        return response.parsedResponse;
    }

    public async deleteBlueprint(params: { accountId: number; blueprintId: string }): Promise<void> {
        await this.apiClient.delete(BLUEPRINT_API.DELETE_BLUEPRINT_URL, params);
    }

    public async getEventBlueprintList(params: { accountId: number }): Promise<IEventBlueprintDTO[]> {
        const response = await this.apiClient.get<IEventBlueprintDTO[]>(BLUEPRINT_API.BLUEPRINT_LIST_URL, { ...params, type: BlueprintType.EVENT });

        return response.parsedResponse;
    }

    public async getMeetingTemplateList(params: { accountId: number }): Promise<IMeetingTemplateDTO[]> {
        const response = await this.apiClient.get<IMeetingTemplateDTO[]>(BLUEPRINT_API.BLUEPRINT_LIST_URL, { ...params, type: BlueprintType.MEETING });

        return response.parsedResponse;
    }

    public async getConnectionTemplateList(params: { accountId: number }): Promise<IConnectionTemplateDTO[]> {
        const response = await this.apiClient.get<IConnectionTemplateDTO[]>(BLUEPRINT_API.BLUEPRINT_LIST_URL, { ...params, type: BlueprintType.CONNECTION });

        return response.parsedResponse;
    }

    public async getOrderTemplateList(params: { accountId: number }): Promise<IOrderTemplateDTO[]> {
        const response = await this.apiClient.get<IOrderTemplateDTO[]>(BLUEPRINT_API.BLUEPRINT_LIST_URL, { ...params, type: BlueprintType.ORDER });

        return response.parsedResponse;
    }

    public async createOrderTemplate(params: ICreateOrderTemplateDTO): Promise<IOrderTemplateDetailsDTO> {
        const response = await this.apiClient.post<IOrderTemplateDetailsDTO>(
            `account/${params.accountId}/blueprint`,
            { ...params, type: BlueprintType.ORDER },
        );

        return response.parsedResponse;
    }

    public async getTempStorageSignedUrl(params: { accountId: number }): Promise<IAssetUrlDTO> {
        const response = await this.apiClient.get<IAssetUrlDTO>(BLUEPRINT_API.TEMP_STORAGE_UPLOAD_URL, params);

        return response.parsedResponse;
    }

    public async uploadFileToTempStorage(file: File, params: { accountId: number }): Promise<IAssetUrlDTO> {
        const assetUrlDTO = await this.getTempStorageSignedUrl(params);
        await this.apiClient.put(assetUrlDTO.assetUrl, file, { ContentType: RequestContentType.txt });

        return assetUrlDTO;
    }

    public async storeFileToStorage(params: { accountId: number } & S3ObjectMetadata): Promise<S3ObjectMetadata> {
        const response = await this.apiClient.put<S3ObjectMetadata>(BLUEPRINT_API.BLUEPRINT_RESOURCES_URL, params);

        return response.parsedResponse;
    }

    public async deleteFileFromStorage(params: { accountId: number; objectKey: string }): Promise<void> {
        const response = await this.apiClient.delete<void>(BLUEPRINT_API.BLUEPRINT_RESOURCES_URL, params);

        return response.parsedResponse;
    }

    public async updateOrderBookingBlueprintData(params: IOrderBookingBlueprintDTO): Promise<IEventBlueprintDTO> {
        const url = `account/${params.accountId}/blueprint/${params.blueprintId}/order-details`;
        const response = await this.apiClient.patch<IEventBlueprintDTO>(url, params);
        return response.parsedResponse;
    }
}

export { BlueprintsDAO };
